/**
 * Camel Up `GameModule` — the platform's entry point for this game.
 *
 * The module advertises:
 *   - identity (id, displayName, min/max players),
 *   - default settings (currently empty — the game has no host-tunable knobs),
 *   - `setupGame`, which sorts lobby players by join time and hands them to
 *     the pure engine,
 *   - the host (TV) and player (phone) gameplay components,
 *   - a `splitInitialState` that strips race-bet colors from the public state
 *     and seeds each player's private subdoc with an empty `raceBets` list
 *     (so subsequent transactional `applyMove` calls always have something to
 *     read),
 *   - `subscribePlayerState` that merges this player's own race-bet colors
 *     back into the streamed state.
 *
 * `TState` is `CamelUpState` directly — same shape across host and player
 * clients (the host gets `null` colors for all face-down bets; the player
 * gets their own colors filled in).
 */

import type { GameModule } from '../module'
import {
  CamelUp,
  type Room,
} from '@mdev/common'
import { HostFinishedView } from './components/HostFinishedView'
import { HostGameView } from './components/HostGameView'
import { PlayerFinishedView } from './components/PlayerFinishedView'
import { PlayerGameView } from './components/PlayerGameView'
import { subscribePlayerState, type CamelUpPrivateDoc } from './service'

/** No host-tunable settings yet — placeholder for future knobs. */
export type CamelUpSettings = Record<string, never>

export type CamelUpState = CamelUp.CamelUpState

export const camelUpModule: GameModule<CamelUpState, CamelUpSettings> = {
  id: 'camel-up',
  displayName: 'Camel Up',
  minPlayers: CamelUp.MIN_PLAYERS,
  maxPlayers: CamelUp.MAX_PLAYERS,
  defaultSettings: {},

  setupGame: (room: Room<CamelUpSettings>) => {
    // Deterministic seating: lobby join order (lastSeenMs), uid tie-break.
    // Same convention as Scotland Yard.
    const players = Object.values(room.players)
      .slice()
      .sort((a, b) => {
        if (a.lastSeenMs !== b.lastSeenMs) return a.lastSeenMs - b.lastSeenMs
        return a.uid < b.uid ? -1 : 1
      })

    return CamelUp.setupGame({
      players: players.map((p) => ({
        uid: p.uid,
        displayName: p.displayName,
        color: p.color,
        avatar: p.avatar,
      })),
    })
  },

  /**
   * Game-start doc split: strip race-bet colors from public (none exist yet
   * at setup time, so this is just a defensive sanitize), and seed an empty
   * private doc per seated player so the transactional `applyMove` can
   * unconditionally read every private/<uid> without missing-doc handling.
   */
  splitInitialState: (state) => {
    const emptyPriv: CamelUpPrivateDoc = { raceBets: [] }
    const privateDocs: Record<string, unknown> = {}
    for (const uid of state.seatOrder) privateDocs[uid] = emptyPriv
    return {
      gameStateDoc: state, // setup state has no race-bet slots yet
      privateDocs,
    }
  },

  subscribePlayerState,

  HostGameView,
  PlayerGameView,
  HostFinishedView,
  PlayerFinishedView,
}

export default camelUpModule
